import { CreditDecision, EFinancingProgram, EPlanType, PlanPromoRule } from '@if/core/types'
import { useMemo } from 'react'

// Local copy of the @if/core promo-filtering hook (relocated out of the shared library);
// retires together with the legacy worksheet screens at the unified worksheet rollout.
const PLAN_ORDERS: Record<EFinancingProgram, EPlanType[]> = {
  [EFinancingProgram.Personal]: [
    EPlanType.OneStar,
    EPlanType.TwoStar,
    EPlanType.ThreeStar,
    EPlanType.FourStar,
    EPlanType.FiveStar,
  ],
  [EFinancingProgram.Auto]: [EPlanType.AutoGold, EPlanType.AutoSilver, EPlanType.AutoBronze],
  [EFinancingProgram.IfXpress]: [
    EPlanType.OneStar,
    EPlanType.TwoStar,
    EPlanType.ThreeStar,
    EPlanType.FourStar,
    EPlanType.FiveStar,
  ],
  [EFinancingProgram.B2c]: [EPlanType.B2CGold, EPlanType.B2CSilver, EPlanType.B2CBronze],
  [EFinancingProgram.CreditRsla]: [EPlanType.CreditRslaGold, EPlanType.CreditRslaSilver, EPlanType.CreditRslaBronze],
  [EFinancingProgram.Products]: [],
  [EFinancingProgram.AutoRsla]: [],
  [EFinancingProgram.MechanicalWarranty]: [],
  [EFinancingProgram.EasyCareProtectionPlan]: [],
}

export const useDisabledPromoCodesList = (
  planPromoRules: PlanPromoRule[] | undefined,
  normsCreditDecision: CreditDecision | null | undefined,
  financingProgramId: EFinancingProgram,
): Set<string> => {
  return useMemo(() => {
    const disabledPromoCodes = new Set<string>()
    if (!planPromoRules || !normsCreditDecision) return disabledPromoCodes
    const { planName: currentPlanName } = normsCreditDecision
    const planOrder = PLAN_ORDERS[financingProgramId]

    if (!planOrder) return disabledPromoCodes

    const currentPlanIndex = planOrder.indexOf(currentPlanName)
    if (currentPlanIndex === -1) return disabledPromoCodes

    const currentPlanEntry = planPromoRules.find((entry) => entry.planName === currentPlanName)

    // Include both default and selectable promo codes from the current plan
    const currentRelatedPromoCodes = [...(currentPlanEntry?.selectablePromoCodes || [])]
    if (currentPlanEntry?.defaultPromoCode) {
      currentRelatedPromoCodes.push(currentPlanEntry.defaultPromoCode)
    }

    // We iterate each higher plan to find disabled promo codes, PLAN ORDERS ensures correct order
    // and we only check plans that are higher than the current one.
    for (let i = currentPlanIndex + 1; i < planOrder.length; i++) {
      const higherPlanName = planOrder[i]
      const higherPlanEntry = planPromoRules.find((entry) => entry.planName === higherPlanName)
      if (higherPlanEntry) {
        if (higherPlanEntry.defaultPromoCode && !currentRelatedPromoCodes.includes(higherPlanEntry.defaultPromoCode)) {
          disabledPromoCodes.add(higherPlanEntry.defaultPromoCode)
        }

        for (const promo of higherPlanEntry.selectablePromoCodes) {
          if (!currentRelatedPromoCodes.includes(promo)) {
            disabledPromoCodes.add(promo)
          }
        }
      }
    }
    return disabledPromoCodes
  }, [planPromoRules, financingProgramId, normsCreditDecision])
}
